import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { css } from "styled-system/css";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" usa el botón rojo de acciones destructivas (eliminar, cancelar, etc). */
  tone?: "default" | "danger";
};

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Reemplaza a window.confirm: un solo diálogo propio del sistema de diseño, montado una vez en la raíz. */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    const opts = typeof options === "string" ? { message: options } : options;
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending(opts);
    });
  }, []);

  function settle(result: boolean) {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal open={pending !== null} onClose={() => settle(false)} title={pending?.title ?? "Confirmar"} size="sm">
        {pending ? (
          <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
            <p className={css({ fontSize: "sm", color: "#334155", lineHeight: "1.6", whiteSpace: "pre-line" })}>
              {pending.message}
            </p>
            <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
              <Button variant="secondary" onClick={() => settle(false)}>
                {pending.cancelLabel ?? "Cancelar"}
              </Button>
              <Button variant={pending.tone === "danger" ? "danger" : "primary"} onClick={() => settle(true)}>
                {pending.confirmLabel ?? "Confirmar"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}

/** `if (!(await confirm("¿Seguro?"))) return;` — reemplazo directo de `window.confirm`. */
export function useConfirm(): ConfirmFn {
  const c = useContext(ConfirmContext);
  if (!c) throw new Error("useConfirm requiere ConfirmDialogProvider");
  return c;
}
